import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {Box, CircularProgress, Link, Stack, Typography} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AssistantMessage, {SUPPORT_CENTER_URL} from './AssistantMessage';
import AssistantTypingIndicator from './AssistantTypingIndicator';
import AssistantComposer from './AssistantComposer';
import AssistantHandoffBanner, {AssistantLongChatHint} from './AssistantHandoffBanner';
import {
    AssistantFeedbackRating,
    AssistantMessage as MessageType,
} from '../assistantMessages';
import type {RagAttachmentDTO} from 'crqlar-react-common/dist/models/ai/ragChat.model';
import type {RagThreadHandoffStatus} from 'crqlar-react-common/dist/models/ai/ragThread.model';
import {Trans, useTranslation} from 'react-i18next';

interface SubmitOptions {
    attachments?: RagAttachmentDTO[];
    humanHandoff?: boolean;
}

interface Props {
    messages: MessageType[];
    isAsking: boolean;
    isLoading?: boolean;
    onSubmit: (text: string, options?: SubmitOptions) => void;
    /** Save thumb rating / explanation for a single message;
     *  pass `rating: null` to clear an existing rating. */
    onFeedback?: (
        messageId: string,
        rating: AssistantFeedbackRating | null,
        explanation?: string,
    ) => void;
    /** Remove a single message from the conversation. */
    onDelete?: (messageId: string) => void;
    /** Re-run the AI for a given assistant message (latest answer only). */
    onRetry?: (messageId: string) => void;
    /** Edit a user message and re-answer (latest user message only). */
    onEdit?: (messageId: string, text: string) => void;
    /** Hand the current question to the support team (offered under an answer the AI couldn't give). */
    onAskTeam?: () => void | Promise<void>;
    /** End the handoff and carry on with the assistant in this same conversation. */
    onBackToAssistant?: () => void | Promise<void>;
    /** Start a fresh conversation — the same flow as the header's new-chat button. */
    onStartNewChat?: () => void;
    /** Stop waiting on the in-flight AI answer. */
    onStop?: () => void;
    /** Id of the freshly-generated answer to reveal with a typing animation. */
    streamingMessageId?: string | null;
    /** Identifies the conversation the composer is attached to. Passed
     *  through so the composer can clear transient tool state (e.g. the
     *  "Ask team" chip) when the conversation changes, while keeping
     *  the draft text + pending screenshots intact. */
    conversationId?: string | null;
    /** The thread's handoff state — the server's routing switch. Drives the banner, and
     *  withdraws the AI-only affordances (regenerate, edit, stop) while the team owns the
     *  conversation, which is also when the backend rejects them. */
    handoffStatus?: RagThreadHandoffStatus;
    /** Focus the composer when this chat surface becomes active. */
    shouldFocusComposer?: boolean;
    /** Incrementing token used to focus while already active. */
    composerFocusToken?: number;
}

/**
 * The Support Center link inside the empty-state sentence. The catalogs store that sentence with a
 * named `<supportLink>` tag — the same pattern as `assistantEmptyIntro`'s `<bold>` — so each language
 * can put the link where its own phrasing wants it. The launch icon rides along inside the component
 * rather than in the string, so no translation has to carry markup for it.
 */
const SupportTeamLink: React.FC<{children?: React.ReactNode}> = ({children}) => (
    <Link
        href={SUPPORT_CENTER_URL}
        target="_blank"
        rel="noreferrer"
        underline="hover"
        sx={{fontWeight: 600, whiteSpace: 'nowrap'}}
    >
        {children}
        <OpenInNewIcon sx={{fontSize: 12, verticalAlign: 'text-bottom', ml: 0.25}}/>
    </Link>
);

/**
 * The chat view inside the panel: scrolling message list with a
 * typing indicator while a response is in flight, plus the composer
 * pinned at the bottom.
 *
 * Doesn't know about the RAG API — it just renders the message array
 * it's given and bubbles user submissions / per-message actions
 * (feedback, delete) back up via callbacks. The parent owns the
 * conversation state via the useAssistantChat hook.
 */
const AssistantChat: React.FC<Props> = ({
    messages,
    isAsking,
    isLoading = false,
    onSubmit,
    onFeedback,
    onDelete,
    onRetry,
    onEdit,
    onAskTeam,
    onBackToAssistant,
    onStartNewChat,
    onStop,
    streamingMessageId,
    conversationId,
    handoffStatus,
    shouldFocusComposer,
    composerFocusToken,
}) => {
    const {t} = useTranslation();
    const teamOwnsConversation = handoffStatus === 'WAITING' || handoffStatus === 'ACTIVE';
    const inHandoff = !!handoffStatus && handoffStatus !== 'NONE';
    const endRef = useRef<HTMLDivElement | null>(null);
    // Sticky-bottom tracking: only auto-scroll when the user is already
    // parked at the bottom. If they've scrolled up to read older messages
    // (or to make room while typing), we don't yank them back down on
    // every messages/isAsking/isLoading change. The same gate keeps
    // future streaming from fighting the user for scroll control.
    const stickyBottomRef = useRef(true);

    const handleScrollAreaScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        stickyBottomRef.current = distanceFromBottom < 60;
    };

    // keep the view pinned to the newest content while an answer reveals,
    // unless the user has scrolled up to read earlier messages
    const followReveal = useCallback(() => {
        if (stickyBottomRef.current) {
            endRef.current?.scrollIntoView({block: 'end'});
        }
    }, []);

    useEffect(() => {
        if (!stickyBottomRef.current) return;
        endRef.current?.scrollIntoView({block: 'end'});
    }, [messages, isAsking, isLoading]);

    // Re-anchor to the bottom when the panel reopens (or a conversation
    // is freshly loaded) — both bump composerFocusToken in the parent.
    // Without this the saved scrollTop is a hair short after the slide
    // transition because layout can shift while the panel was off-screen
    // (poll updates, composer re-focus, etc.).
    useEffect(() => {
        if (!stickyBottomRef.current) return;
        const id = requestAnimationFrame(() => {
            endRef.current?.scrollIntoView({block: 'end'});
        });
        return () => cancelAnimationFrame(id);
    }, [composerFocusToken]);

    // The backend only allows regenerating the latest answer and editing the
    // latest user message, so surface those affordances on the last turn only.
    const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : undefined;
    const lastMessage = messages.length > 0 ? messages[messages.length - 1] : undefined;
    const lastUserMessageId = [...messages].reverse().find(m => m.role === 'user')?.id;
    // Only the newest answer keeps its action row on screen; older ones reveal it on hover, so a long
    // conversation reads as a conversation instead of a column of button rows.
    const lastAnswerId = [...messages].reverse().find(m => m.role === 'assistant')?.id;
    const bottomContentNeedsRoom = lastMessage?.role === 'assistant' || isAsking || (isLoading && messages.length > 0);
    const bottomBreathingRoom = bottomContentNeedsRoom ? '96px' : '16px';

    // Which questions went to the team instead of the assistant. Read off the answer that follows each
    // one rather than stored on the question: a message routed to a human gets no AI reply at all, so
    // either a human answered it, or nothing has answered it yet and the team currently owns the
    // conversation. (`WAITING_FOR_HUMAN` only appears in threads from before the server took over
    // routing, where placeholders stood in for the pending reply.)
    const teamQuestionIds = useMemo(() => {
        const ids = new Set<string>();
        messages.forEach((m, index) => {
            if (m.role !== 'user') return;
            const answer = messages.slice(index + 1).find(next => next.role === 'assistant');
            if (!answer) {
                if (teamOwnsConversation && !m.isLocalOnly) ids.add(m.id);
                return;
            }
            if (
                answer.status === 'WAITING_FOR_HUMAN' ||
                answer.origin === 'ADMIN' ||
                answer.origin === 'SLACK'
            ) {
                ids.add(m.id);
            }
        });
        return ids;
    }, [messages, teamOwnsConversation]);

    // "Ask the team" belongs to the current turn only: the backend hands over the latest question the
    // customer asked, which stops being this answer's question as soon as they send another message.
    // NEEDS_CONTEXT deliberately doesn't show the CTA: the assistant is mid-intake, and escalating
    // there would hand the team the same vague question the diagnostic questions exist to sharpen.
    // The composer's "Ask team" path stays available throughout.
    const askTeamMessageId = onAskTeam && !teamOwnsConversation && lastMessage?.role === 'assistant'
        && lastMessage.origin === 'AI' && lastMessage.answerOutcome === 'NOT_FOUND'
        ? lastMessage.id
        : undefined;

    return (
        <>
            <Box
                onScroll={handleScrollAreaScroll}
                sx={{flex: 1, overflowY: 'auto', px: 2.5, py: 2}}
            >
                {messages.length === 0 && isLoading && (
                    <Stack alignItems="center" justifyContent="center" sx={{minHeight: 180, color: 'text.secondary'}}>
                        <CircularProgress
                            size={24}
                            thickness={3}
                            sx={{animationDuration: '1.8s'}}
                        />
                    </Stack>
                )}

                {messages.length === 0 && !isAsking && !isLoading && (
                    <Box sx={{mt: 6, mx: 'auto', maxWidth: 460, px: 1}}>
                        <Typography sx={{fontSize: 14, fontWeight: 600, color: 'text.primary', mb: 1.5}}>
                            {t("Hi! I'm your new assistant")}
                        </Typography>
                        <Typography sx={{fontSize: 13, lineHeight: 1.5, color: 'text.secondary'}}>
                            <Trans
                                i18nKey="assistantEmptyIntro"
                                components={{bold: <strong/>}}
                            />
                        </Typography>
                        <Typography sx={{fontSize: 13, lineHeight: 1.5, color: 'text.secondary', mt: 2}}>
                            <Trans
                                i18nKey="assistantEmptyAskTeam"
                                components={{supportLink: <SupportTeamLink/>}}
                            />
                        </Typography>
                    </Box>
                )}

                <Stack spacing={3} sx={{pb: bottomBreathingRoom}}>
                    {messages.map(m => (
                        <AssistantMessage
                            key={m.id}
                            message={m}
                            viaTeam={teamQuestionIds.has(m.id)}
                            onFeedback={
                                onFeedback
                                    ? (rating, explanation) =>
                                          onFeedback(m.id, rating, explanation)
                                    : undefined
                            }
                            onDelete={onDelete ? () => onDelete(m.id) : undefined}
                            onRetry={
                                onRetry
                                && m.id === lastMessageId
                                && m.role === 'assistant'
                                && m.origin === 'AI'
                                && !teamOwnsConversation
                                    ? () => onRetry(m.id)
                                    : undefined
                            }
                            onEdit={
                                onEdit
                                && m.role === 'user'
                                && m.id === lastUserMessageId
                                && !teamOwnsConversation
                                    ? (text: string) => onEdit(m.id, text)
                                    : undefined
                            }
                            onAskTeam={m.id === askTeamMessageId ? onAskTeam : undefined}
                            keepActionsVisible={m.id === lastAnswerId}
                            animate={m.role === 'assistant' && m.id === streamingMessageId}
                            onReveal={followReveal}
                        />
                    ))}
                    {messages.length > 0 && isLoading && (
                        <Stack alignItems="center" sx={{py: 0.5}}>
                            <CircularProgress
                                size={18}
                                thickness={3}
                                sx={{animationDuration: '1.8s'}}
                            />
                        </Stack>
                    )}
                    {/* Nothing is composing a reply when the message is on its way to a person —
                        the indicator's "reading the docs…" patter would be a small lie. */}
                    {isAsking && !teamOwnsConversation && <AssistantTypingIndicator/>}
                </Stack>

                <Box ref={endRef} sx={{height: 0}}/>
            </Box>

            {/* One notice above the composer at most: what the team is doing with this conversation
                (plus the way back to the assistant), or — in AI mode — the nudge to start fresh. */}
            {inHandoff ? (
                <AssistantHandoffBanner
                    status={handoffStatus}
                    onBackToAssistant={onBackToAssistant}
                    onStartNewChat={onStartNewChat}
                />
            ) : (
                <AssistantLongChatHint
                    messageCount={messages.length}
                    onStartNewChat={onStartNewChat}
                />
            )}

            <AssistantComposer
                onSubmit={onSubmit}
                disabled={isAsking || isLoading}
                asking={isAsking}
                onStop={onStop}
                conversationId={conversationId}
                teamOwnsConversation={teamOwnsConversation}
                shouldFocus={shouldFocusComposer}
                focusToken={composerFocusToken}
            />
        </>
    );
};

export default AssistantChat;
